import {
  DEFAULT_LICENSE_LEVEL,
  KNOWN_FEATURES,
  LICENSE_LEVELS,
  featuresOfLevel,
  levelLabelOf,
} from '@innermatelic/shared';
import { useEffect, useState } from 'react';
import {
  FilterSelect,
  ListToolbar,
  Pager,
  SearchBox,
  useListView,
} from '../../components/ListControls.js';
import { type CustomerRow, type LicenseRow, api } from '../../lib/api.js';

/** feature 키 → 짧은 표기(라벨의 괄호 앞부분). 목록/표에 사용. */
function shortFeatureLabel(key: string): string {
  const f = KNOWN_FEATURES.find((x) => x.key === key);
  return f ? f.label.replace(/\s*\(.*$/, '') : key;
}

/** features[] → 사람이 읽는 표기. ['*'] = 전체. */
function renderFeatures(features: string[]) {
  if (features.length === 1 && features[0] === '*') return <span>전체</span>;
  return (
    <>
      {features.map((k, i) => (
        <span key={k}>
          {i > 0 ? ', ' : ''}
          <span className={k === 'studio' ? 'text-brand-600 font-medium' : ''}>
            {shortFeatureLabel(k)}
          </span>
        </span>
      ))}
    </>
  );
}

/** 등급 표기. 카탈로그에 없으면 키 그대로, 없으면 「사용자 지정」. */
function renderLevel(level: string | null) {
  if (!level) return <span className="text-neutral-500">사용자 지정</span>;
  const label = levelLabelOf(level);
  return <span className="font-medium">{label ?? level}</span>;
}

/** 등급 select 의 「사용자 지정」 값. 빈 문자열은 select 에서 다루기 번거로워 명시 토큰을 쓴다. */
const CUSTOM_LEVEL = '__custom__';

function initialFeatureSel(): Record<string, boolean> {
  const sel: Record<string, boolean> = {};
  for (const f of KNOWN_FEATURES) sel[f.key] = f.defaultOn;
  return sel;
}

export function Licenses() {
  const [licenses, setLicenses] = useState<LicenseRow[]>([]);
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [form, setForm] = useState({
    customerId: '',
    type: 'subscription' as 'perpetual' | 'subscription',
    expiresAt: '',
    maxActivations: 1,
    maxUsers: '',
    notes: '',
  });
  // 등급 선택. CUSTOM_LEVEL 이면 아래 기능 체크박스 경로로 떨어진다.
  const [level, setLevel] = useState<string>(DEFAULT_LICENSE_LEVEL);
  // 발급 기능 선택(사용자 지정일 때만): 와일드카드(전체) 또는 개별 체크박스.
  const [wildcard, setWildcard] = useState(false);
  const [featureSel, setFeatureSel] = useState<Record<string, boolean>>(initialFeatureSel);
  const [justIssued, setJustIssued] = useState<LicenseRow | null>(null);
  // 목록 필터 — 상태(기본 active — 지금 쓰는 것부터)·종류·등급·고객사. 검색은 고객사·키·등급·메모(예: 체험 VM 이름)
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired' | 'revoked'>(
    'active',
  );
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'perpetual' | 'subscription'>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');

  async function reload() {
    try {
      const [l, c] = await Promise.all([
        api.get<{ licenses: LicenseRow[] }>('/api/admin/licenses'),
        api.get<{ customers: CustomerRow[] }>('/api/admin/customers'),
      ]);
      setLicenses(l.licenses);
      setCustomers(c.customers);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function issue(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const isCustom = level === CUSTOM_LEVEL;
    // 등급을 고르면 features 는 서버가 카탈로그에서 파생한다. 여기서 보내는 값은 스키마 통과용.
    const features = isCustom
      ? wildcard
        ? ['*']
        : KNOWN_FEATURES.filter((f) => featureSel[f.key]).map((f) => f.key)
      : (featuresOfLevel(level) ?? []);
    if (features.length === 0) {
      setError('최소 1개 이상의 기능을 선택하세요.');
      return;
    }
    try {
      const body: Record<string, unknown> = {
        customerId: form.customerId,
        type: form.type,
        maxActivations: form.maxActivations,
        level: isCustom ? null : level,
        features,
      };
      if (form.type === 'subscription' && form.expiresAt) {
        body.expiresAt = new Date(form.expiresAt).toISOString();
      }
      const maxUsersTrimmed = form.maxUsers.trim();
      if (maxUsersTrimmed) {
        const n = Number.parseInt(maxUsersTrimmed, 10);
        if (Number.isFinite(n) && n > 0) body.maxUsers = n;
      }
      if (form.notes) body.notes = form.notes;
      const res = await api.post<{ license: LicenseRow }>('/api/admin/licenses', body);
      setJustIssued(res.license);
      setIssuing(false);
      setForm({
        customerId: '',
        type: 'subscription',
        expiresAt: '',
        maxActivations: 1,
        maxUsers: '',
        notes: '',
      });
      setLevel(DEFAULT_LICENSE_LEVEL);
      setWildcard(false);
      setFeatureSel(initialFeatureSel());
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function revoke(id: string) {
    if (!confirm('이 라이선스를 revoke 하시겠습니까? 새 활성화가 차단됩니다.')) return;
    try {
      await api.post(`/api/admin/licenses/${id}/revoke`, {});
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const customerMap = new Map(customers.map((c) => [c.id, c]));
  const statusOf = (l: LicenseRow) =>
    l.revokedAt
      ? 'revoked'
      : l.expiresAt && new Date(l.expiresAt).getTime() < Date.now()
        ? 'expired'
        : 'active';
  const filteredLicenses = licenses.filter(
    (l) =>
      (statusFilter === 'all' || statusOf(l) === statusFilter) &&
      (typeFilter === 'all' || l.type === typeFilter) &&
      (levelFilter === 'all' ||
        (levelFilter === CUSTOM_LEVEL ? !l.level : l.level === levelFilter)) &&
      (customerFilter === 'all' || l.customerId === customerFilter),
  );
  const list = useListView(filteredLicenses, (l) =>
    [
      customerMap.get(l.customerId)?.name ?? '',
      l.licenseKey,
      l.level ? (levelLabelOf(l.level) ?? l.level) : '사용자 지정',
      l.notes ?? '',
    ].join(' '),
  );

  return (
    <div>
      <header className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">라이선스</h1>
        <button
          type="button"
          onClick={() => setIssuing((v) => !v)}
          disabled={customers.length === 0}
          className="px-3 py-1.5 bg-brand-400 hover:bg-brand-600 disabled:bg-neutral-300 text-white text-sm font-medium rounded-md"
        >
          {issuing ? '취소' : '+ 새 라이선스 발급'}
        </button>
      </header>

      {customers.length === 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
          먼저 <strong>고객사</strong> 페이지에서 고객을 등록하세요.
        </div>
      )}

      {issuing && (
        <form
          onSubmit={issue}
          className="mb-6 p-5 bg-surface border border-neutral-200 rounded-lg space-y-3"
        >
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">고객사 *</span>
            <select
              value={form.customerId}
              onChange={(e) => setForm({ ...form, customerId: e.target.value })}
              required
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              <option value="">선택…</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">종류</span>
            <select
              value={form.type}
              onChange={(e) =>
                setForm({ ...form, type: e.target.value as 'perpetual' | 'subscription' })
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              <option value="subscription">구독 (만료 있음)</option>
              <option value="perpetual">영구</option>
            </select>
          </label>

          {form.type === 'subscription' && (
            <label className="block">
              <span className="block text-xs font-medium text-neutral-700 mb-1">
                만료일 (비우면 기본 1년)
              </span>
              <input
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </label>
          )}

          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">
              최대 활성화 횟수 (rehost 정책)
            </span>
            <input
              type="number"
              min={1}
              max={10}
              value={form.maxActivations}
              onChange={(e) =>
                setForm({ ...form, maxActivations: Number.parseInt(e.target.value, 10) || 1 })
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </label>

          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">
              최대 사용자 수 (비우면 무제한)
            </span>
            <input
              type="number"
              min={1}
              value={form.maxUsers}
              onChange={(e) => setForm({ ...form, maxUsers: e.target.value })}
              placeholder="무제한"
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </label>

          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">등급</span>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              {LICENSE_LEVELS.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.label}
                </option>
              ))}
              <option value={CUSTOM_LEVEL}>사용자 지정 (기능 직접 선택)</option>
            </select>
          </label>

          {level !== CUSTOM_LEVEL && (
            <div className="rounded-md bg-neutral-50 border border-neutral-200 p-3 text-xs text-neutral-600">
              <div className="mb-1">{LICENSE_LEVELS.find((l) => l.key === level)?.description}</div>
              <div>
                포함 기능:{' '}
                <span className="text-neutral-800">
                  {(featuresOfLevel(level) ?? []).map(shortFeatureLabel).join(', ')}
                </span>
              </div>
            </div>
          )}

          {level === CUSTOM_LEVEL && (
            <fieldset className="block">
              <span className="block text-xs font-medium text-neutral-700 mb-1">기능</span>
              <label className="flex items-center gap-2 text-sm py-1">
                <input
                  type="checkbox"
                  checked={wildcard}
                  onChange={(e) => setWildcard(e.target.checked)}
                />
                <span className="font-medium">전체 기능 (*)</span>
              </label>
              <div className="pl-6 space-y-1">
                {KNOWN_FEATURES.map((f) => (
                  <label
                    key={f.key}
                    className={`flex items-center gap-2 text-sm py-0.5 ${
                      wildcard ? 'opacity-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={wildcard}
                      checked={wildcard ? true : (featureSel[f.key] ?? false)}
                      onChange={(e) =>
                        setFeatureSel((prev) => ({ ...prev, [f.key]: e.target.checked }))
                      }
                    />
                    <span>{f.label}</span>
                  </label>
                ))}
              </div>
              <p className="pl-6 pt-1 text-xs text-neutral-500">
                「전체 기능 (*)」 은 <strong>발급 시점의 전체</strong>다. 이후 신설되는 유료 기능은
                포함되지 않는다.
              </p>
            </fieldset>
          )}

          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">노트</span>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="계약 번호, PO 등"
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </label>

          <div className="pt-2">
            <button
              type="submit"
              className="px-4 py-2 bg-brand-400 hover:bg-brand-600 text-white text-sm font-medium rounded-md"
            >
              발급
            </button>
          </div>
        </form>
      )}

      {justIssued && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-800 font-medium">라이선스 발급 완료</p>
          <div className="mt-3 text-sm">
            <span className="text-neutral-700">고객에게 전달할 license key:</span>
            <div className="mt-1 px-3 py-2 bg-surface border border-green-300 rounded font-mono text-base font-semibold tracking-wide">
              {justIssued.licenseKey}
            </div>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(justIssued.licenseKey);
              }}
              className="mt-2 px-3 py-1 bg-neutral-200 hover:bg-neutral-300 text-neutral-800 text-xs rounded"
            >
              복사
            </button>
            <button
              type="button"
              onClick={() => setJustIssued(null)}
              className="mt-2 ml-2 px-3 py-1 text-neutral-600 hover:text-neutral-900 text-xs"
            >
              닫기
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      <ListToolbar
        total={list.total}
        unit="건"
        searching={!!list.query.trim()}
        size={list.size}
        onSize={list.setSize}
      >
        <SearchBox
          value={list.query}
          onChange={list.setQuery}
          placeholder="고객사·키·등급·메모 검색"
        />
        <FilterSelect
          label="상태"
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            list.resetPage();
          }}
          options={[
            { value: 'all', label: '상태: 전체' },
            { value: 'active', label: 'active' },
            { value: 'expired', label: 'expired' },
            { value: 'revoked', label: 'revoked' },
          ]}
        />
        <FilterSelect
          label="종류"
          value={typeFilter}
          onChange={(v) => {
            setTypeFilter(v);
            list.resetPage();
          }}
          options={[
            { value: 'all', label: '종류: 전체' },
            { value: 'perpetual', label: '영구' },
            { value: 'subscription', label: '구독' },
          ]}
        />
        <FilterSelect
          label="등급"
          value={levelFilter}
          onChange={(v) => {
            setLevelFilter(v);
            list.resetPage();
          }}
          options={[
            { value: 'all', label: '등급: 전체' },
            ...LICENSE_LEVELS.map((lv) => ({ value: lv.key as string, label: lv.label as string })),
            { value: CUSTOM_LEVEL, label: '사용자 지정' },
          ]}
        />
        <FilterSelect
          label="고객사"
          value={customerFilter}
          onChange={(v) => {
            setCustomerFilter(v);
            list.resetPage();
          }}
          options={[
            { value: 'all', label: '고객사: 전체' },
            ...customers.map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
      </ListToolbar>

      {/* 칸이 좁아 글자가 줄바꿈되지 않게 — 셀은 한 줄로, 화면이 좁으면 표만 가로 스크롤 */}
      <div className="bg-surface border border-neutral-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-medium">고객사</th>
              <th className="text-left px-4 py-2 font-medium">License Key</th>
              <th className="text-left px-4 py-2 font-medium">등급</th>
              <th className="text-left px-4 py-2 font-medium">종류</th>
              <th className="text-left px-4 py-2 font-medium">만료</th>
              <th className="text-left px-4 py-2 font-medium">활성화</th>
              <th className="text-left px-4 py-2 font-medium">최대 사용자</th>
              <th className="text-left px-4 py-2 font-medium">기능</th>
              <th className="text-left px-4 py-2 font-medium">상태</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {list.pageItems.map((l) => {
              const customer = customerMap.get(l.customerId);
              const expired = l.expiresAt && new Date(l.expiresAt).getTime() < Date.now();
              return (
                <tr key={l.id} className="hover:bg-neutral-50/50">
                  <td className="px-4 py-2">
                    <div className="font-medium">{customer?.name ?? '—'}</div>
                    {l.notes && <div className="text-xs text-neutral-500">{l.notes}</div>}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{l.licenseKey}</td>
                  <td className="px-4 py-2 text-neutral-700 text-xs">{renderLevel(l.level)}</td>
                  <td className="px-4 py-2 text-neutral-600">
                    {l.type === 'perpetual' ? '영구' : '구독'}
                  </td>
                  <td className="px-4 py-2 text-neutral-600 text-xs">
                    {l.expiresAt ? new Date(l.expiresAt).toLocaleDateString('ko-KR') : '—'}
                  </td>
                  <td className="px-4 py-2 text-neutral-600">
                    {l.activationCount} / {l.maxActivations}
                  </td>
                  <td className="px-4 py-2 text-neutral-600">
                    {l.maxUsers != null ? `${l.maxUsers}명` : '무제한'}
                  </td>
                  <td className="px-4 py-2 text-neutral-600 text-xs whitespace-normal min-w-[200px]">
                    {renderFeatures(l.features)}
                  </td>
                  <td className="px-4 py-2">
                    {l.revokedAt ? (
                      <span className="text-red-700 text-xs font-medium">revoked</span>
                    ) : expired ? (
                      <span className="text-amber-700 text-xs font-medium">expired</span>
                    ) : (
                      <span className="text-green-700 text-xs font-medium">active</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {!l.revokedAt && (
                      <button
                        type="button"
                        onClick={() => revoke(l.id)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {list.total === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-neutral-500">
                  {licenses.length === 0
                    ? '아직 발급된 라이선스 없음'
                    : '조건에 맞는 라이선스가 없습니다.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pager page={list.page} pages={list.pages} onPage={list.setPage} />
    </div>
  );
}
